import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, Circle } from 'lucide-react';
import { Button } from '@/components/ui';
import { Logo } from '@/features/auth/auth-layout';
import progress from '../../../../docs/PROGRESS.md?raw';

export const Route = createFileRoute('/dev/progress')({ component: ProgressPage });

interface Phase {
  title: string;
  items: { done: boolean; text: string; notes: string[] }[];
}

function parse(md: string): Phase[] {
  const phases: Phase[] = [];
  for (const line of md.split('\n')) {
    const heading = /^## (.+)/.exec(line);
    if (heading) {
      phases.push({ title: heading[1]!, items: [] });
      continue;
    }
    const current = phases.at(-1);
    if (!current) continue;
    const item = /^- \[( |x)\] (.+)/.exec(line);
    if (item) current.items.push({ done: item[1] === 'x', text: item[2]!, notes: [] });
    else if (/^\s{2,}\S/.test(line) && current.items.length)
      current.items.at(-1)!.notes.push(line.trim().replace(/^- /, ''));
  }
  return phases;
}

/** Build status, straight from docs/PROGRESS.md. */
function ProgressPage() {
  const phases = parse(progress);
  const all = phases.flatMap((p) => p.items);
  const done = all.filter((i) => i.done).length;
  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <Button asChild size="sm" variant="ghost" className="mb-6">
        <Link to="/">
          <ArrowLeft /> Back to app
        </Link>
      </Button>
      <div className="flex items-center gap-3">
        <Logo className="size-9" />
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Regent build progress</h1>
          <p className="text-sm text-fg-muted">
            {done} of {all.length} items done
          </p>
        </div>
      </div>
      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-hover">
        <div
          className="h-full rounded-full bg-accent"
          style={{ width: `${all.length ? (done / all.length) * 100 : 0}%` }}
        />
      </div>
      {phases.map((phase) => {
        const count = phase.items.filter((i) => i.done).length;
        return (
          <section key={phase.title} className="mt-8">
            <h2 className="mb-2 flex items-baseline gap-2 text-sm font-semibold">
              {phase.title}
              <span className="font-normal text-fg-subtle">
                {count}/{phase.items.length}
              </span>
            </h2>
            <ul className="space-y-1">
              {phase.items.map((item) => (
                <li key={item.text} className="flex items-start gap-2 text-sm">
                  {item.done ? (
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                  ) : (
                    <Circle className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
                  )}
                  <span className={item.done ? '' : 'text-fg-muted'}>
                    {item.text}
                    {item.notes.map((n) => (
                      <span key={n} className="block text-xs text-fg-subtle">
                        {n}
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
